import React, { useContext, useEffect, useMemo, useState } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import styles from '../../../styles/components/Modal/LeadSettings/LeadTableSettingsModal.module.css';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';

const translations = {
    ru: {
        title: 'Настройки таблицы',
        subtitle: 'Перетаскивайте колонки, чтобы поменять порядок, и снимайте галочки, чтобы скрыть их.',
        cancel: 'Отмена',
        save: 'Сохранить',
        reset: 'Сбросить',
    },
    en: {
        title: 'Table settings',
        subtitle: 'Drag columns to change order and uncheck them to hide them.',
        cancel: 'Cancel',
        save: 'Save',
        reset: 'Reset',
    },
};

const moveItem = (items, fromIndex, toIndex) => {
    const nextItems = [...items];
    const [movedItem] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, movedItem);
    return nextItems;
};

export const LeadTableSettingsModal = ({ isOpen, onClose, columns, preferences, onSave }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;
    const isDark = theme === 'dark';
    const [draftPreferences, setDraftPreferences] = useState(preferences);
    const [draggedKey, setDraggedKey] = useState(null);

    useEffect(() => {
        setDraftPreferences(preferences);
    }, [preferences]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const orderedColumns = useMemo(() => {
        const columnsMap = new Map(columns.map((column) => [column.key, column]));
        return draftPreferences.order.map((key) => columnsMap.get(key)).filter(Boolean);
    }, [columns, draftPreferences]);

    if (!isOpen) {
        return null;
    }

    const visibleSet = new Set(draftPreferences.visible);

    const handleToggleColumn = (columnKey) => {
        setDraftPreferences((prevPreferences) => {
            const isVisible = prevPreferences.visible.includes(columnKey);

            if (isVisible && prevPreferences.visible.length === 1) {
                return prevPreferences;
            }

            return {
                ...prevPreferences,
                visible: isVisible
                    ? prevPreferences.visible.filter((key) => key !== columnKey)
                    : [...prevPreferences.visible, columnKey],
            };
        });
    };

    const handleReset = () => {
        const columnKeys = columns.map((column) => column.key);
        setDraftPreferences({
            order: columnKeys,
            visible: columnKeys,
        });
    };

    const handleDragStart = (columnKey) => {
        setDraggedKey(columnKey);
    };

    const handleDrop = (targetKey) => {
        if (!draggedKey || draggedKey === targetKey) {
            setDraggedKey(null);
            return;
        }

        setDraftPreferences((prevPreferences) => {
            const fromIndex = prevPreferences.order.indexOf(draggedKey);
            const toIndex = prevPreferences.order.indexOf(targetKey);

            if (fromIndex === -1 || toIndex === -1) {
                return prevPreferences;
            }

            return {
                ...prevPreferences,
                order: moveItem(prevPreferences.order, fromIndex, toIndex),
            };
        });

        setDraggedKey(null);
    };

    const handleSave = () => {
        onSave(draftPreferences);
    };

    return (
        <div className={`${styles.overlay} ${isDark ? styles.darkOverlay : ''}`} onClick={onClose}>
            <div
                className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={`${styles.header} ${isDark ? styles.darkHeader : ''}`}>
                    <div>
                        <h3 className={`${styles.title} ${isDark ? styles.darkTitle : ''}`}>{currentTranslation.title}</h3>
                        <p className={`${styles.subtitle} ${isDark ? styles.darkSubtitle : ''}`}>{currentTranslation.subtitle}</p>
                    </div>
                    <button
                        type="button"
                        className={`${styles.closeButton} ${isDark ? styles.darkCloseButton : ''}`}
                        onClick={onClose}
                        aria-label={currentTranslation.cancel}
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className={styles.list}>
                    {orderedColumns.map((column) => {
                        const isVisible = visibleSet.has(column.key);

                        return (
                            <div
                                key={column.key}
                                className={`${styles.row} ${draggedKey === column.key ? styles.draggingRow : ''} ${isDark ? styles.darkRow : ''}`}
                                draggable
                                onDragStart={() => handleDragStart(column.key)}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={() => handleDrop(column.key)}
                                onDragEnd={() => setDraggedKey(null)}
                            >
                                <div className={styles.rowMain}>
                                    <span className={`${styles.dragHandle} ${isDark ? styles.darkDragHandle : ''}`}>
                                        <DragIndicatorIcon fontSize="small" />
                                    </span>
                                    <label className={styles.checkboxLabel}>
                                        <span className={`${styles.columnLabel} ${isDark ? styles.darkColumnLabel : ''}`}>{column.label}</span>
                                        <input
                                            type="checkbox"
                                            checked={isVisible}
                                            onChange={() => handleToggleColumn(column.key)}
                                            className={styles.checkboxInput}
                                        />
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className={`${styles.footer} ${isDark ? styles.darkFooter : ''}`}>
                    <button type="button" className={`${styles.secondaryButton} ${isDark ? styles.darkSecondaryButton : ''}`} onClick={handleReset}>
                        {currentTranslation.reset}
                    </button>
                    <div className={styles.footerActions}>
                        <button type="button" className={`${styles.secondaryButton} ${isDark ? styles.darkSecondaryButton : ''}`} onClick={onClose}>
                            {currentTranslation.cancel}
                        </button>
                        <button type="button" className={styles.primaryButton} onClick={handleSave}>
                            {currentTranslation.save}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
